/**
 * Polaroid stack: fanned deck, hover/scroll expand, fullscreen reels viewer.
 */
class PolaroidStack extends HTMLElement {
  #booted = false;
  #teardown = null;

  connectedCallback() {
    if (this.#booted) return;

    const stage = this.querySelector('[data-polaroid-stage]');
    const cards = [...this.querySelectorAll('[data-polaroid]')];
    const dotsWrap = this.querySelector('[data-polaroid-dots]');
    const viewer = this.querySelector('[data-polaroid-viewer]');
    const viewerScroller = this.querySelector('[data-polaroid-viewer-scroller]');
    const viewerClose = this.querySelector('[data-polaroid-viewer-close]');

    if (!stage) return;

    // Nested theme blocks may mount after the host.
    if (cards.length < 1) {
      requestAnimationFrame(() => {
        if (!this.#booted) this.connectedCallback();
      });
      return;
    }

    this.#booted = true;

    const DUR = Number(this.dataset.duration) || 700;
    const PAUSE = Number(this.dataset.pause) || 300;
    const EASE = 'cubic-bezier(.45,.05,.25,1)';
    const autoplay = this.dataset.autoplay !== 'false';
    const viewerEnabled = this.dataset.viewer !== 'false';
    const fan = this.dataset.fan || 'even';
    const orientation = this.dataset.orientation === 'left' ? 'left' : 'right';
    const fanDir = orientation === 'left' ? -1 : 1;
    const count = cards.length;
    const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const canExpand = !autoplay && !reduceMotion;
    const desktopHover =
      canExpand && matchMedia('(hover: hover) and (pointer: fine)').matches;
    const scrollReveal = canExpand && !desktopHover;

    const collapsedSpread = readUnit(this.dataset.collapsedSpread, 40, 0, 160) / 100;
    const expandedSpread = readUnit(this.dataset.expandedSpread, 100, 0, 160) / 100;
    const upwardPeek = readUnit(this.dataset.upwardPeek, 70, 0, 150) / 100;
    const frontTilt = readUnit(this.dataset.frontTilt, 4.5, 0, 12);

    let order = cards.map((_, i) => i);
    let busy = false;
    let timer = null;
    let expanded = false;
    let wantExpanded = false;
    let viewerOpen = false;
    let viewerBusy = false;
    let looping = false;
    let lastFocused = null;
    let visibilityObserver = null;
    let scrollEndTimer = null;

    const dots = buildDots(dotsWrap, count, (i) => goTo(i));

    const easeOutCubic = (t) => 1 - (1 - t) ** 3;
    const easeInCubic = (t) => t * t * t;

    const cascading = { x: [-3, 44], y: [3, -36], r: 24, s: 0.05 };
    const fanRanges = {
      even: { x: [-2, 40], y: [2, -34], r: 22, s: 0.045 },
      cascading,
      reverse_cascading: cascading,
      tight: { x: [-1, 26], y: [1, -22], r: 14, s: 0.03 },
      wide: { x: [-4, 52], y: [3, -40], r: 28, s: 0.055 },
    };

    const spreadFor = (want) => {
      if (!canExpand) return 1;
      return want ? expandedSpread : collapsedSpread;
    };

    const progressForSlot = (slot) => {
      if (count <= 1) return 0;
      const linear = slot / (count - 1);
      switch (fan) {
        case 'cascading':
          return easeInCubic(linear);
        case 'reverse_cascading':
          return easeOutCubic(linear);
        case 'tight':
          return easeOutCubic(linear) * 0.85;
        case 'wide':
          return linear ** 0.85;
        default:
          return linear;
      }
    };

    const slotPos = (slot, want = expanded) => {
      const range = fanRanges[fan] || fanRanges.even;
      const t = progressForSlot(slot);
      const spread = spreadFor(want);
      const xBoost = t * 12 * spread;
      const x = (range.x[0] + t * range.x[1] * spread + xBoost) * fanDir;
      const y = range.y[0] + t * range.y[1] * spread * upwardPeek;
      const r = (-frontTilt + t * range.r * spread) * fanDir;
      const s = 1 - t * range.s * Math.min(spread, 1);
      const shadowAlpha = 0.26 - t * 0.12 * Math.min(spread, 1);

      return {
        transform: `translate(${x.toFixed(2)}%, ${y.toFixed(2)}%) rotate(${r.toFixed(2)}deg) scale(${s.toFixed(3)})`,
        z: 100 - slot,
        shadow: `drop-shadow(0 ${(18 - t * 8).toFixed(1)}px ${(24 - t * 8).toFixed(1)}px rgba(60,19,97,${shadowAlpha.toFixed(2)}))`,
      };
    };

    const peelMidTransform = () =>
      `translate(${-34 * fanDir}%, 0%) rotate(${-6 * fanDir}deg) scale(0.95)`;

    const applyResting = () => {
      order.forEach((cardIdx, slot) => {
        const el = cards[cardIdx];
        const pos = slotPos(slot);
        el.style.transform = pos.transform;
        el.style.zIndex = String(pos.z);
        el.style.filter = pos.shadow;
      });
      dots.forEach((dot, i) => dot.classList.toggle('on', i === order[0]));
    };

    const finishCycle = (nextOrder) => {
      order = nextOrder;
      applyResting();
      cards.forEach((card) => card.getAnimations().forEach((a) => a.cancel()));
      busy = false;
      this.classList.remove('is-busy');
      syncExpanded();
    };

    const syncExpanded = () => {
      if (!canExpand || busy || wantExpanded === expanded) return;
      expanded = wantExpanded;
      applyResting();
    };

    const setExpanded = (next) => {
      if (!canExpand) return;
      wantExpanded = next;
      syncExpanded();
    };

    const stop = () => {
      if (!timer) return;
      clearInterval(timer);
      timer = null;
    };

    const play = () => {
      stop();
      if (!autoplay || viewerOpen || reduceMotion) return;
      timer = setInterval(advance, DUR + PAUSE);
    };

    const animateStackShift = (deltaSlot, easing = EASE) => {
      order.forEach((cardIdx, slot) => {
        const skip = deltaSlot < 0 ? slot === 0 : slot === count - 1;
        if (skip) return;
        const el = cards[cardIdx];
        const from = slotPos(slot);
        const to = slotPos(slot + deltaSlot);
        el.animate(
          [
            { transform: from.transform, filter: from.shadow },
            { transform: to.transform, filter: to.shadow },
          ],
          { duration: DUR, easing, fill: 'forwards' }
        );
      });
    };

    const advance = () => {
      if (busy || count < 2 || viewerOpen) return;
      busy = true;
      this.classList.add('is-busy');

      const nextOrder = [...order.slice(1), order[0]];
      const front = cards[order[0]];
      const frontPos = slotPos(0);
      const exitPos = slotPos(count - 1);
      const mid = peelMidTransform();

      const exit = front.animate(
        [
          { transform: frontPos.transform, filter: frontPos.shadow, offset: 0 },
          { transform: mid, filter: exitPos.shadow, offset: 0.5 },
          { transform: exitPos.transform, filter: exitPos.shadow, offset: 1 },
        ],
        { duration: DUR, easing: EASE, fill: 'forwards' }
      );

      front.style.zIndex = '200';
      setTimeout(() => {
        order.forEach((cardIdx, slot) => {
          if (slot === 0) return;
          cards[cardIdx].style.zIndex = String(slotPos(slot - 1).z);
        });
        front.style.zIndex = String(exitPos.z);
      }, DUR * 0.3);

      animateStackShift(-1);
      exit.addEventListener('finish', () => finishCycle(nextOrder), { once: true });
    };

    /** Reverse of advance: back → peel mid → front (same easing). */
    const retreat = () => {
      if (busy || count < 2 || viewerOpen) return;
      busy = true;
      this.classList.add('is-busy');

      const nextOrder = [order[count - 1], ...order.slice(0, count - 1)];
      const incoming = cards[order[count - 1]];
      const fromBack = slotPos(count - 1);
      const toFront = slotPos(0);
      const mid = peelMidTransform();
      const startZ = fromBack.z;
      const endZ = 200;
      const liftFrom = 0.6;

      incoming.style.zIndex = String(startZ);
      const enter = incoming.animate(
        [
          { transform: fromBack.transform, filter: fromBack.shadow, offset: 0 },
          { transform: mid, filter: toFront.shadow, offset: 0.5 },
          { transform: toFront.transform, filter: toFront.shadow, offset: 1 },
        ],
        { duration: DUR, easing: EASE, fill: 'forwards' }
      );

      const liftZ = () => {
        const progress = enter.effect?.getComputedTiming?.().progress;
        if (typeof progress === 'number') {
          if (progress < liftFrom) {
            incoming.style.zIndex = String(startZ);
          } else {
            const t = (progress - liftFrom) / (1 - liftFrom);
            incoming.style.zIndex = String(Math.round(startZ + (endZ - startZ) * t));
          }
        }
        if (busy && progress != null && progress < 1) requestAnimationFrame(liftZ);
      };
      requestAnimationFrame(liftZ);

      setTimeout(() => {
        order.forEach((cardIdx, slot) => {
          if (slot === count - 1) return;
          cards[cardIdx].style.zIndex = String(slotPos(slot + 1).z);
        });
      }, DUR * liftFrom);

      animateStackShift(1);
      enter.addEventListener('finish', () => finishCycle(nextOrder), { once: true });
    };

    const goTo = (index) => {
      if (busy || index === order[0]) return;
      stop();
      let guard = 0;
      while (order[0] !== index && guard < count) {
        order = [...order.slice(1), order[0]];
        guard += 1;
      }
      applyResting();
      play();
    };

    const bump = (dir = 1) => {
      stop();
      if (dir < 0) retreat();
      else advance();
      play();
    };

    // Prev retreats (bring back card forward); next advances (send front to back).
    this.querySelector('[data-polaroid-next]')?.addEventListener('click', () => bump(1));
    this.querySelector('[data-polaroid-prev]')?.addEventListener('click', () => bump(-1));

    const onHoverEnter = () => setExpanded(true);
    const onHoverLeave = () => setExpanded(false);

    if (canExpand) this.classList.add('polaroid-stack--hover-expand');

    if (desktopHover) {
      this.addEventListener('pointerenter', onHoverEnter);
      this.addEventListener('pointerleave', onHoverLeave);
      this.addEventListener('focusin', onHoverEnter);
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) setExpanded(false);
      });
    }

    if (scrollReveal) {
      visibilityObserver = new IntersectionObserver(
        ([entry]) => {
          if (!entry) return;
          setExpanded(entry.isIntersecting && entry.intersectionRatio >= 0.28);
        },
        { threshold: [0, 0.28, 0.45, 0.7, 1], rootMargin: '0px 0px -10% 0px' }
      );
      visibilityObserver.observe(this);
    }

    /* ----- Fullscreen reels viewer ----- */
    const slideHeight = () => viewerScroller?.clientHeight || window.innerHeight;
    const domIndexFor = (realIndex) => (count > 1 ? realIndex + 1 : realIndex);

    const scrollToDomIndex = (domIndex, behavior = 'auto') => {
      if (!viewerScroller) return;
      const top = Math.max(0, domIndex) * slideHeight();
      if (behavior === 'smooth' && !reduceMotion) {
        viewerScroller.scrollTo({ top, behavior: 'smooth' });
        return;
      }
      viewerScroller.style.scrollBehavior = 'auto';
      viewerScroller.scrollTop = top;
    };

    const scrollToSlide = (realIndex, behavior = 'auto') => {
      scrollToDomIndex(domIndexFor(realIndex), behavior);
    };

    const currentDomIndex = () => {
      if (!viewerScroller) return count > 1 ? 1 : 0;
      return Math.round(viewerScroller.scrollTop / (slideHeight() || 1));
    };

    const normalizeLoop = () => {
      if (!viewerScroller || count < 2 || looping) return;
      const dom = currentDomIndex();
      if (dom <= 0) {
        looping = true;
        scrollToDomIndex(count, 'auto');
        looping = false;
      } else if (dom >= count + 1) {
        looping = true;
        scrollToDomIndex(1, 'auto');
        looping = false;
      }
    };

    const buildViewerSlides = () => {
      if (!viewerScroller) return;
      viewerScroller.replaceChildren();
      if (count < 1) return;
      if (count > 1) viewerScroller.appendChild(makeSlide(cards[count - 1], count - 1));
      cards.forEach((card, i) => viewerScroller.appendChild(makeSlide(card, i)));
      if (count > 1) viewerScroller.appendChild(makeSlide(cards[0], 0));
    };

    const openViewer = (startIndex = 0) => {
      if (!viewerEnabled || !viewer || !viewerScroller || viewerOpen) return;
      buildViewerSlides();

      if (count > 1 && !viewer.querySelector('.polaroid-viewer__hint')) {
        const hint = document.createElement('p');
        hint.className = 'polaroid-viewer__hint';
        hint.textContent = 'Scroll for more';
        viewer.appendChild(hint);
      }

      lastFocused = document.activeElement;
      viewerOpen = true;
      stop();
      viewer.hidden = false;
      document.body.classList.add('polaroid-viewer-open');
      viewer.classList.remove('is-hint-hidden');
      if (viewer.parentElement !== document.body) document.body.appendChild(viewer);

      requestAnimationFrame(() => {
        scrollToSlide(startIndex, 'auto');
        viewerClose?.focus();
      });
      setTimeout(() => viewer.classList.add('is-hint-hidden'), 2200);
    };

    const closeViewer = () => {
      if (!viewer || !viewerOpen) return;
      viewerOpen = false;
      viewer.hidden = true;
      document.body.classList.remove('polaroid-viewer-open');
      viewerScroller?.replaceChildren();
      play();
      lastFocused?.focus?.();
    };

    const stepViewer = (dir) => {
      if (!viewerOpen || viewerBusy || count < 2) return;
      viewerBusy = true;
      scrollToDomIndex(currentDomIndex() + dir, reduceMotion ? 'auto' : 'smooth');
      setTimeout(() => {
        normalizeLoop();
        viewerBusy = false;
      }, reduceMotion ? 50 : 480);
    };

    viewerClose?.addEventListener('click', closeViewer);

    const onViewerScroll = () => {
      if (!viewerOpen || looping) return;
      clearTimeout(scrollEndTimer);
      scrollEndTimer = setTimeout(normalizeLoop, 60);
    };
    viewerScroller?.addEventListener('scroll', onViewerScroll, { passive: true });
    viewerScroller?.addEventListener('scrollend', normalizeLoop);

    const onKeydown = (e) => {
      if (!viewerOpen) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        closeViewer();
      } else if (e.key === 'ArrowDown' || e.key === 'PageDown') {
        e.preventDefault();
        stepViewer(1);
      } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault();
        stepViewer(-1);
      }
    };
    window.addEventListener('keydown', onKeydown);

    if (viewerEnabled) {
      cards.forEach((card, index) => {
        card.setAttribute('role', 'button');
        card.setAttribute('tabindex', '0');
        card.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          openViewer(index);
        });
      });
    }

    /* ----- Deck swipe / tap ----- */
    let sx = 0;
    let sy = 0;
    let dragging = false;
    let axis = null;
    let pointerCard = null;

    stage.addEventListener('pointerdown', (e) => {
      dragging = true;
      axis = null;
      sx = e.clientX;
      sy = e.clientY;
      pointerCard = e.target.closest('[data-polaroid]');
    });

    const onMove = (e) => {
      if (!dragging) return;
      const dx = e.clientX - sx;
      const dy = e.clientY - sy;
      if (axis === null && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
        axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
    };

    const onUp = (e) => {
      if (!dragging) return;
      dragging = false;
      const moved = Math.hypot(e.clientX - sx, e.clientY - sy);

      if (axis === 'x') {
        const dx = e.clientX - sx;
        if (Math.abs(dx) < 45) {
          pointerCard = null;
          return;
        }
        const advanceSwipe = orientation === 'right' ? dx < 0 : dx > 0;
        bump(advanceSwipe ? 1 : -1);
      } else if (viewerEnabled && moved < 10 && pointerCard) {
        const index = cards.indexOf(pointerCard);
        if (index >= 0) openViewer(index);
      }

      pointerCard = null;
    };

    const onCancel = () => {
      dragging = false;
      pointerCard = null;
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);

    const onVisibilityChange = () => {
      if (document.hidden) stop();
      else play();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    this.#teardown = () => {
      stop();
      closeViewer();
      visibilityObserver?.disconnect();
      clearTimeout(scrollEndTimer);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('keydown', onKeydown);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (desktopHover) {
        this.removeEventListener('pointerenter', onHoverEnter);
        this.removeEventListener('pointerleave', onHoverLeave);
        this.removeEventListener('focusin', onHoverEnter);
      }
    };

    applyResting();
    play();
  }

  disconnectedCallback() {
    this.#teardown?.();
    this.#teardown = null;
    this.#booted = false;
  }
}

function readUnit(value, fallback, min, max) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

function buildDots(wrap, count, onSelect) {
  if (!wrap) return [];
  wrap.replaceChildren();
  for (let i = 0; i < count; i += 1) {
    const dot = document.createElement('button');
    dot.type = 'button';
    dot.className = 'polaroid-stack__dot';
    dot.setAttribute('aria-label', String(i + 1));
    dot.addEventListener('click', () => onSelect(i));
    wrap.appendChild(dot);
  }
  return [...wrap.children];
}

function makeSlide(card, index) {
  const src =
    card.dataset.fullSrc ||
    card.querySelector('img')?.currentSrc ||
    card.querySelector('img')?.src ||
    '';
  const srcset = card.dataset.fullSrcset || '';
  const caption = card.dataset.caption || card.getAttribute('aria-label') || '';

  const slide = document.createElement('article');
  slide.className = 'polaroid-viewer__slide';
  slide.dataset.viewerIndex = String(index);

  const media = document.createElement('div');
  media.className = 'polaroid-viewer__media';

  if (src) {
    const img = document.createElement('img');
    img.src = src;
    if (srcset) img.srcset = srcset;
    img.sizes = '100vw';
    img.alt = caption || 'Polaroid photo';
    img.decoding = 'async';
    img.draggable = false;
    media.appendChild(img);
  }

  slide.appendChild(media);

  if (caption) {
    const cap = document.createElement('p');
    cap.className = 'polaroid-viewer__caption';
    cap.textContent = caption;
    slide.appendChild(cap);
  }

  return slide;
}

if (!customElements.get('polaroid-stack')) {
  customElements.define('polaroid-stack', PolaroidStack);
}
